/**
 * DevTools Inspector - Unified runtime introspection surface.
 *
 * Aggregates live state from all registered diagnostic channels into a
 * single snapshot that developer tools, status panels, and the AI
 * reasoning chain can query without coupling to individual subsystems.
 *
 * Data sources:
 *   - Runtime metrics (action counts, error rates, latency p95)
 *   - Runtime snapshot (module statuses, component inventory)
 *   - Execution timeline (recent operation records)
 *   - Event history (recent event bus traffic)
 *   - State inspector (current state values and registry)
 *   - Editor engine (open documents, cursor states, dirty state)
 *   - Module health (per-module health status)
 *
 * All reads are non-destructive and side-effect-free. No subsystem
 * is mutated by an inspection call.
 */

import { logger }           from "@/system/logger";
import { captureSnapshot }  from "@/diagnostics/runtimeInspector";
import { getMetrics }       from "@/diagnostics/runtimeMetrics";
import { getTimeline }      from "@/diagnostics/executionTimeline";
import { eventBus }         from "@/runtime/eventBus";
import { inspectState }     from "@/diagnostics/stateInspector";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const editorEngine: any = new Proxy({}, { get: () => () => ({ openDocuments: 0, totalPatches: 0, activeCursors: 0, memoryEstimateKB: 0 }) });
import { nuvraRuntime }     from "@/runtime/coreRuntime";
import { commandBus }       from "@/commands/commandBus";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ModuleHealthSummary {
  moduleId: string;
  name:     string;
  status:   "healthy" | "degraded" | "unhealthy" | "unknown";
  detail?:  string;
}

export interface EditorInspection {
  openDocuments:    number;
  dirtyDocuments:   number;
  activeCursors:    number;
  memoryEstimateKB: number;
  documents: Array<{
    docId:    string;
    path:     string;
    version:  number;
    status:   string;
    language: string;
  }>;
}

export interface CommandBusInspection {
  registeredTypes: string[];
  inflightCount:   number;
}

export interface DevToolsSnapshot {
  capturedAt:       number;
  runtimePhase:     string;
  metrics:          ReturnType<typeof getMetrics>;
  runtimeSnapshot:  ReturnType<typeof captureSnapshot>;
  timeline:         ReturnType<typeof getTimeline>;
  recentEvents:     ReturnType<typeof eventBus.getHistory>;
  stateReport:      ReturnType<typeof inspectState>;
  editor:           EditorInspection;
  commandBus:       CommandBusInspection;
  moduleHealth:     ModuleHealthSummary[];
  logEntries:       unknown[];
}

// ── Inspector ─────────────────────────────────────────────────────────────────

class DevToolsInspectorImpl {
  private _snapshotCount = 0;

  /**
   * Capture a full DevTools snapshot. All reads are non-destructive.
   * Expensive in proportion to open document count and history depth.
   */
  capture(opts: { eventLimit?: number; timelineLimit?: number } = {}): DevToolsSnapshot {
    const { eventLimit = 50, timelineLimit = 100 } = opts;
    this._snapshotCount += 1;

    const runtimeSnapshot = captureSnapshot();
    const metrics         = getMetrics();
    const timeline        = getTimeline().slice(-timelineLimit);
    const recentEvents    = eventBus.getHistory().slice(-eventLimit);
    const stateReport     = inspectState();
    const editorStats     = editorEngine.getStats();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const openDocs: any[] = editorEngine.getOpenDocuments();

    const moduleHealth: ModuleHealthSummary[] = nuvraRuntime
      .getRegisteredModules()
      .map((m) => {
        const h = nuvraRuntime.checkModuleHealth(m.id);
        return {
          moduleId: m.id,
          name:     m.name,
          status:   h?.status ?? "unknown",
          detail:   h?.detail,
        };
      });

    const editor: EditorInspection = {
      openDocuments:    editorStats.openDocuments,
      dirtyDocuments:   openDocs.filter(d => d.status === "modified").length,
      activeCursors:    editorStats.activeCursors,
      memoryEstimateKB: editorStats.memoryEstimateKB,
      documents:        openDocs.map(d => ({
        docId:    d.docId,
        path:     d.path,
        version:  d.version,
        status:   d.status,
        language: d.language,
      })),
    };

    const cmdBusInspection: CommandBusInspection = {
      registeredTypes: commandBus.getRegisteredTypes(),
      inflightCount:   commandBus.getInflightCount(),
    };

    return {
      capturedAt:      Date.now(),
      runtimePhase:    "running",
      metrics,
      runtimeSnapshot,
      timeline,
      recentEvents,
      stateReport,
      editor,
      commandBus:      cmdBusInspection,
      moduleHealth,
      logEntries:      [],
    };
  }

  /** Number of snapshots taken since the inspector was created. */
  getSnapshotCount(): number { return this._snapshotCount; }

  /**
   * Quick health check: returns overall system health from module statuses.
   * Does not take a full snapshot.
   */
  quickHealth(): "healthy" | "degraded" | "unhealthy" | "unknown" {
    const modules = nuvraRuntime.getRegisteredModules();
    if (modules.length === 0) return "unknown";

    let hasUnhealthy = false;
    let hasDegraded  = false;
    let allUnknown   = true;

    for (const m of modules) {
      const h = nuvraRuntime.checkModuleHealth(m.id);
      const s = h?.status ?? "unknown";
      if (s !== "unknown") allUnknown = false;
      if (s === "unhealthy") hasUnhealthy = true;
      if (s === "degraded")  hasDegraded  = true;
    }

    if (allUnknown)   return "unknown";
    if (hasUnhealthy) return "unhealthy";
    if (hasDegraded)  return "degraded";
    return "healthy";
  }
}

export const devToolsInspector = new DevToolsInspectorImpl();
